import * as React from "react";
import {
    makeStyles,
    Button,
    Popover,
    PopoverSurface,
    Input,
    Tooltip,
    PopoverTrigger,
    tokens,
} from "@fluentui/react-components";
import { useState } from "react";
import { DialpadRegular, DialpadFilled } from "@fluentui/react-icons";
import { Dialpad } from "@azure/communication-react";
import { useIsMobile } from "../../hooks/useIsMobile";

/* ----------------------------------
* Utility: Validate DTMF characters
* Only allow 0–9, * and #
* ---------------------------------- */
export const isValidDtmfChar = (char) => /^[0-9*#]$/.test(char);

const fixedBottomPanelRow2ColBtnHoverStyles = {
  color: tokens.colorBrandBackgroundHover,
  backgroundColor: "white",
}

const useStyles = makeStyles({
    dialpadWrapper: {
        padding: "6px",
        borderRadius: "12px",
    },
    inputWrapper: {
        marginBottom: "10px",
        width: "96%",
    },
    fixedBottomPanelRow2ColBtn: {
        backgroundColor: "white",
        borderRadius: "2rem",
        color: tokens.colorNeutralForeground4,
        ':hover': fixedBottomPanelRow2ColBtnHoverStyles,
        ':active': fixedBottomPanelRow2ColBtnHoverStyles,
        ':hover:active': fixedBottomPanelRow2ColBtnHoverStyles,
        ':focus': fixedBottomPanelRow2ColBtnHoverStyles,
    },
    fixedBottomPanelRow2ColBtnDisabled: {
        color: tokens.colorNeutralForegroundDisabled,
    },
});

/* ----------------------------------
* Dialpad Content
* ---------------------------------- */
const ExampleContent = ({handleDtmf, activeConnectedCallObj}) => {
    const [dialNumber, setDialNumber] = useState("");
    const isMobile = useIsMobile();
    const styles = useStyles();

    /* ----------------------------------
     * Handle manual keyboard input
     * - Allow only valid DTMF keys
     * - Trigger DTMF immediately
     * - Block all other keys (letters, backspace, etc.)
     * ---------------------------------- */
    const handleKeyDown = (event) => {
        const key = event.key;

        if (isValidDtmfChar(key)) {
            console.log('render one')
            handleDtmf(key, activeConnectedCallObj); // Send DTMF tone
            setDialNumber((prev) => prev + key); // Update UI
        }

        // Prevent default behavior for ALL keys
        event.preventDefault();
    };

    return (
        <div className={styles.dialpadWrapper}>
            {/* -----------------------------
       * Input field (read-only logic)
       * ----------------------------- */}
            <Input
                className={styles.inputWrapper}
                appearance="underline"
                value={dialNumber}
                placeholder="Enter number"
                onKeyDown={handleKeyDown}
            />

            {/* -----------------------------
       * Dialpad Component
       * ----------------------------- */}
            <Dialpad
                dialpadMode="dtmf"
                longPressTrigger={isMobile ? "touch" : "mouseAndTouch"}
                //Prevent local audible DTMF to avoid in-band pickup
                disableDtmfPlayback={true}
                onClickDialpadButton={(buttonValue) => {
                    //Allow only valid DTMF characters
                    if (isValidDtmfChar(buttonValue)) {
                        handleDtmf(buttonValue, activeConnectedCallObj); //Send tone
                        setDialNumber((prev) => prev + buttonValue); //Update UI
                    }
                }}
                styles={{
                    root: {
                        background: "#ffffff",
                    },
                    button: {
                        root: {
                            height: isMobile ? "4rem" : "3rem",
                            width: isMobile ? "5rem" : "4rem",
                            lineHeight: "inherit",
                            background: "#ffffff",
                        },
                        rootHovered: {
                            background: "#e5e7eb",
                            span: {
                                color: "#0f6cbd !important",
                            },
                        },
                        rootPressed: {
                            background: "#d1d5db",
                            color: "#0f6cbd",
                        },
                    },
                    digit: {
                        color: "#0f6cbd",
                        fontWeight: 600,
                    },
                    letter: {
                        color: "#0f6cbd",
                        fontWeight: 600,
                    },
                }}
            />
        </div>
    );
};

/* ----------------------------------
* Popover Wrapper for Dialpad Button
* ---------------------------------- */
export const DtmfDialpad = (props) => {
    const styles = useStyles();
    return (
      <Popover positioning="below-end" {...props}>
          <PopoverTrigger disableButtonEnhancement>
              <Tooltip content="Dialpad" relationship="label">
                  <Button
                      size="medium"
                      icon={<DialpadFilled />}
                      disabled={props.disabled}
                      aria-label="Open dialpad"
                      className={styles.fixedBottomPanelRow2ColBtn}
                      disabledClassName={styles.fixedBottomPanelRow2ColBtnDisabled}
                  />
              </Tooltip>
          </PopoverTrigger>

          <PopoverSurface tabIndex={-1}>
              <ExampleContent handleDtmf={props.handleDtmf} activeConnectedCallObj={props.activeConnectedCallObj}/>
          </PopoverSurface>
      </Popover>
  );
};
